import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { 
  ShoppingCart, 
  Plus, 
  Trash2, 
  UserPlus, 
  CreditCard,
  ArrowLeft,
  Check,
  Loader2,
  AlertCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Generate session ID for cart tracking
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('ol-cart-session');
  if (!sessionId) {
    sessionId = `session-${Date.now()}-${Math.random().toString(36).substring(7)}`;
    sessionStorage.setItem('ol-cart-session', sessionId);
  }
  return sessionId;
};

export default function RegistrationCart({ orgId, onClose, onComplete }) {
  const { user } = useAuth();
  const { isDark } = useTheme();
  const [cart, setCart] = useState(null);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [step, setStep] = useState('returning-check'); // returning-check, cart, add-player, family-info, checkout
  
  // Returning family lookup
  const [lookupEmail, setLookupEmail] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [returningData, setReturningData] = useState(null);
  
  // New player form
  const [playerForm, setPlayerForm] = useState({
    first_name: '',
    last_name: '',
    birthday: '',
    gender: 'male',
    team_id: ''
  });
  
  // Family form (persists across players)
  const [familyForm, setFamilyForm] = useState({
    name: '',
    street: '',
    city: '',
    state: '',
    zip: '',
    contact_name: '',
    contact_phone: '',
    contact_email: ''
  });
  
  const [submitting, setSubmitting] = useState(false);
  const sessionId = getSessionId();

  useEffect(() => {
    fetchCart();
    fetchTeams();
  }, [orgId]);

  const fetchCart = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/cart/${orgId}?session_id=${sessionId}`);
      setCart(data);
      
      // Pre-fill family form if cart has family data
      if (data.family_data?.name) {
        setFamilyForm({
          name: data.family_data.name || '',
          street: data.family_data.address?.street || '',
          city: data.family_data.address?.city || '',
          state: data.family_data.address?.state || '',
          zip: data.family_data.address?.zip || '',
          contact_name: data.family_data.contacts?.[0]?.name || '',
          contact_phone: data.family_data.contacts?.[0]?.phone || '',
          contact_email: data.family_data.contacts?.[0]?.email || ''
        });
      }
    } catch (err) {
      setError('Failed to load cart');
    } finally {
      setLoading(false);
    }
  };

  const fetchTeams = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/organizations/${orgId}/teams`);
      setTeams(data);
    } catch (err) {
      console.error('Failed to fetch teams:', err);
    }
  };

  const addToCart = async () => {
    if (!playerForm.first_name || !playerForm.last_name || !playerForm.birthday || !playerForm.team_id) {
      setError('Please fill in all required fields');
      return;
    }
    
    setSubmitting(true);
    setError('');
    
    try {
      const { data } = await axios.post(
        `${API_URL}/api/cart/${orgId}/items?session_id=${sessionId}`,
        {
          player_data: {
            first_name: playerForm.first_name,
            last_name: playerForm.last_name,
            birthday: playerForm.birthday,
            gender: playerForm.gender,
            health_status: { is_healthy: true }
          },
          team_id: playerForm.team_id,
          season: '2025-2026',
          answers: [],
          documents_agreed: ['waiver-1']
        }
      );
      
      setCart(data);
      setPlayerForm({ first_name: '', last_name: '', birthday: '', gender: 'male', team_id: '' });
      setStep('cart');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to add player');
    } finally {
      setSubmitting(false);
    }
  };

  const removeFromCart = async (itemId) => {
    try {
      await axios.delete(`${API_URL}/api/cart/${orgId}/items/${itemId}?session_id=${sessionId}`);
      fetchCart();
    } catch (err) {
      setError('Failed to remove item');
    }
  };

  const updateFamilyData = async () => {
    if (!familyForm.name || !familyForm.contact_name || !familyForm.contact_phone) {
      setError('Please fill in required family information');
      return;
    }
    
    setSubmitting(true);
    setError('');
    
    try {
      await axios.put(
        `${API_URL}/api/cart/${orgId}/family?session_id=${sessionId}`,
        {
          name: familyForm.name,
          address: {
            street: familyForm.street,
            city: familyForm.city,
            state: familyForm.state,
            zip: familyForm.zip
          },
          contacts: [{
            name: familyForm.contact_name,
            relationship: 'Parent',
            phone: familyForm.contact_phone,
            email: familyForm.contact_email,
            is_emergency: true
          }]
        }
      );
      
      setStep('checkout');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save family info');
    } finally {
      setSubmitting(false);
    }
  };

  const processCheckout = async () => {
    setSubmitting(true);
    setError('');
    
    try {
      const { data } = await axios.post(
        `${API_URL}/api/cart/${orgId}/checkout?session_id=${sessionId}`
      );
      
      // Clear session
      sessionStorage.removeItem('ol-cart-session');
      
      if (onComplete) {
        onComplete(data);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Checkout failed');
    } finally {
      setSubmitting(false);
    }
  };

  const lookupReturningFamily = async () => {
    if (!lookupEmail) return;
    setLookupLoading(true);
    setError('');
    try {
      const { data } = await axios.get(
        `${API_URL}/api/families/lookup/${orgId}?email=${encodeURIComponent(lookupEmail)}`
      );
      if (data.found) {
        setReturningData(data);
        // Pre-fill family form from historical data
        const fam = data.family;
        if (fam) {
          setFamilyForm({
            name: fam.last_name || fam.name || '',
            street: fam.address?.street || '',
            city: fam.address?.city || '',
            state: fam.address?.state || '',
            zip: fam.address?.zip || '',
            contact_name: fam.contacts?.[0]?.name || '',
            contact_phone: fam.contacts?.[0]?.phone || '',
            contact_email: fam.contacts?.[0]?.email || lookupEmail,
          });
        }
      } else {
        setReturningData(null);
      }
      setStep('cart');
    } catch (err) {
      setError('Lookup failed. Starting fresh registration.');
      setStep('cart');
    } finally {
      setLookupLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
      </div>
    );
  }

  // Returning Family Check Step
  if (step === 'returning-check') {
    return (
      <div className="space-y-4" data-testid="returning-check">
        <Card className={`${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
          <CardHeader>
            <CardTitle className={isDark ? 'text-white' : 'text-slate-900'}>
              <UserPlus className="h-5 w-5 inline mr-2" />
              Welcome to Registration
            </CardTitle>
            <CardDescription className={isDark ? 'text-slate-400' : 'text-slate-500'}>
              Have you registered with this organization before?
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {error && (
              <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-3 flex items-center gap-2 text-red-400">
                <AlertCircle className="h-4 w-4" />
                <span className="text-sm">{error}</span>
              </div>
            )}
            {returningData && (
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 text-green-400 text-sm">
                <Check className="h-4 w-4 inline mr-1" />
                {returningData.message} Your information has been pre-filled.
              </div>
            )}
            <div className="space-y-3">
              <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>
                Enter your email to check for previous registration
              </Label>
              <div className="flex gap-2">
                <Input
                  type="email"
                  value={lookupEmail}
                  onChange={(e) => setLookupEmail(e.target.value)}
                  placeholder="your@email.com"
                  className={`flex-1 ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300'}`}
                  data-testid="returning-email-input"
                />
                <Button
                  onClick={lookupReturningFamily}
                  disabled={lookupLoading || !lookupEmail}
                  className="bg-amber-600 hover:bg-amber-700"
                  data-testid="lookup-btn"
                >
                  {lookupLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Look Up'}
                </Button>
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                className={`flex-1 ${isDark ? 'border-slate-600 text-slate-300' : ''}`}
                onClick={() => setStep('cart')}
                data-testid="new-family-btn"
              >
                New Family — Start Fresh
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Add Player Step
  if (step === 'add-player') {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => setStep('cart')}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to Cart
          </Button>
        </div>
        
        <Card className={`${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
          <CardHeader>
            <CardTitle className={isDark ? 'text-white' : 'text-slate-900'}>
              <UserPlus className="h-5 w-5 inline mr-2" />
              Add Player
            </CardTitle>
            <CardDescription className={isDark ? 'text-slate-400' : 'text-slate-500'}>
              Register a new player for the season
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {error && (
              <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-3 flex items-center gap-2 text-red-400">
                <AlertCircle className="h-4 w-4" />
                <span className="text-sm">{error}</span>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>First Name *</Label>
                <Input
                  value={playerForm.first_name}
                  onChange={(e) => setPlayerForm({...playerForm, first_name: e.target.value})}
                  placeholder="First name"
                  className={isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300'}
                />
              </div>
              <div className="space-y-2">
                <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Last Name *</Label>
                <Input
                  value={playerForm.last_name}
                  onChange={(e) => setPlayerForm({...playerForm, last_name: e.target.value})}
                  placeholder="Last name"
                  className={isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300'}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Date of Birth *</Label>
                <Input
                  type="date"
                  value={playerForm.birthday}
                  onChange={(e) => setPlayerForm({...playerForm, birthday: e.target.value})}
                  className={isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300'}
                />
              </div>
              <div className="space-y-2">
                <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Gender *</Label>
                <select
                  value={playerForm.gender}
                  onChange={(e) => setPlayerForm({...playerForm, gender: e.target.value})}
                  className={`w-full h-10 rounded-md px-3 ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300 text-slate-900'} border`}
                >
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Select Team *</Label>
              <select
                value={playerForm.team_id}
                onChange={(e) => setPlayerForm({...playerForm, team_id: e.target.value})}
                className={`w-full h-10 rounded-md px-3 ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300 text-slate-900'} border`}
              >
                <option value="">-- Select a team --</option>
                {teams.map(team => (
                  <option key={team.id} value={team.id}>
                    {team.name} - ${team.season_cost} (Max Age: {team.eligibility?.max_age})
                  </option>
                ))}
              </select>
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="ghost" onClick={() => setStep('cart')}>
                Cancel
              </Button>
              <Button 
                onClick={addToCart}
                disabled={submitting}
                className="bg-amber-600 hover:bg-amber-700"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add to Cart'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Family Info Step
  if (step === 'family-info') {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => setStep('cart')}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to Cart
          </Button>
        </div>
        
        <Card className={`${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
          <CardHeader>
            <CardTitle className={isDark ? 'text-white' : 'text-slate-900'}>
              Family Information
            </CardTitle>
            <CardDescription className={isDark ? 'text-slate-400' : 'text-slate-500'}>
              This information will apply to all players in your cart
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {error && (
              <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-3 flex items-center gap-2 text-red-400">
                <AlertCircle className="h-4 w-4" />
                <span className="text-sm">{error}</span>
              </div>
            )}
            
            <div className="space-y-2">
              <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Family Name *</Label>
              <Input
                value={familyForm.name}
                onChange={(e) => setFamilyForm({...familyForm, name: e.target.value})}
                placeholder="Smith Family"
                className={isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300'}
              />
            </div>
            
            <div className="space-y-2">
              <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Street Address</Label>
              <Input
                value={familyForm.street}
                onChange={(e) => setFamilyForm({...familyForm, street: e.target.value})}
                placeholder="123 Main St"
                className={isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300'}
              />
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>City</Label>
                <Input
                  value={familyForm.city}
                  onChange={(e) => setFamilyForm({...familyForm, city: e.target.value})}
                  placeholder="Cary"
                  className={isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300'}
                />
              </div>
              <div className="space-y-2">
                <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>State</Label>
                <Input
                  value={familyForm.state}
                  onChange={(e) => setFamilyForm({...familyForm, state: e.target.value})}
                  placeholder="NC"
                  className={isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300'}
                />
              </div>
              <div className="space-y-2">
                <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>ZIP</Label>
                <Input
                  value={familyForm.zip}
                  onChange={(e) => setFamilyForm({...familyForm, zip: e.target.value})}
                  placeholder="27519"
                  className={isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300'}
                />
              </div>
            </div>
            
            <hr className={isDark ? 'border-slate-700' : 'border-slate-200'} />
            
            <h4 className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Primary Contact (Emergency)
            </h4>
            
            <div className="space-y-2">
              <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Contact Name *</Label>
              <Input
                value={familyForm.contact_name}
                onChange={(e) => setFamilyForm({...familyForm, contact_name: e.target.value})}
                placeholder="John Smith"
                className={isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300'}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Phone *</Label>
                <Input
                  type="tel"
                  value={familyForm.contact_phone}
                  onChange={(e) => setFamilyForm({...familyForm, contact_phone: e.target.value})}
                  placeholder="919-555-0100"
                  className={isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300'}
                />
              </div>
              <div className="space-y-2">
                <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Email</Label>
                <Input
                  type="email"
                  value={familyForm.contact_email}
                  onChange={(e) => setFamilyForm({...familyForm, contact_email: e.target.value})}
                  placeholder="john@example.com"
                  className={isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300'}
                />
              </div>
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="ghost" onClick={() => setStep('cart')}>
                Cancel
              </Button>
              <Button 
                onClick={updateFamilyData}
                disabled={submitting}
                className="bg-amber-600 hover:bg-amber-700"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Continue to Checkout'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Checkout Step
  if (step === 'checkout') {
    return (
      <div className="space-y-4">
        <Card className={`${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
          <CardHeader>
            <CardTitle className={isDark ? 'text-white' : 'text-slate-900'}>
              <CreditCard className="h-5 w-5 inline mr-2" />
              Complete Registration
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {error && (
              <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-3 flex items-center gap-2 text-red-400">
                <AlertCircle className="h-4 w-4" />
                <span className="text-sm">{error}</span>
              </div>
            )}
            
            <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-700' : 'bg-slate-100'}`}>
              <h4 className={`font-medium mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Order Summary
              </h4>
              {cart?.items?.map((item, idx) => (
                <div key={idx} className={`flex justify-between py-2 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                  <span>{item.player_data?.first_name} {item.player_data?.last_name} - {item.team_name}</span>
                  <span>${item.amount?.toFixed(2)}</span>
                </div>
              ))}
              <hr className={`my-2 ${isDark ? 'border-slate-600' : 'border-slate-300'}`} />
              <div className={`flex justify-between font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                <span>Total</span>
                <span>${cart?.total?.toFixed(2)}</span>
              </div>
            </div>
            
            <div className={`p-4 rounded-lg ${isDark ? 'bg-green-900/20 border-green-700' : 'bg-green-50 border-green-200'} border`}>
              <p className={`text-sm ${isDark ? 'text-green-400' : 'text-green-700'}`}>
                <Check className="h-4 w-4 inline mr-1" />
                Family information saved. Click below to complete registration.
              </p>
            </div>
            
            <div className="flex justify-between pt-4">
              <Button variant="ghost" onClick={() => setStep('family-info')}>
                <ArrowLeft className="h-4 w-4 mr-1" /> Back
              </Button>
              <Button 
                onClick={processCheckout}
                disabled={submitting}
                className="bg-green-600 hover:bg-green-700"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Complete Registration'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Cart View (default)
  return (
    <div className="space-y-4">
      <Card className={`${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className={isDark ? 'text-white' : 'text-slate-900'}>
              <ShoppingCart className="h-5 w-5 inline mr-2" />
              Registration Cart
            </CardTitle>
            <CardDescription className={isDark ? 'text-slate-400' : 'text-slate-500'}>
              {cart?.item_count || 0} player(s) in cart
            </CardDescription>
          </div>
          <Button 
            onClick={() => setStep('add-player')}
            className="bg-amber-600 hover:bg-amber-700"
          >
            <Plus className="h-4 w-4 mr-1" /> Add Player
          </Button>
        </CardHeader>
        <CardContent>
          {cart?.items?.length === 0 ? (
            <div className={`text-center py-8 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              <ShoppingCart className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>Your cart is empty</p>
              <p className="text-sm">Add players to begin registration</p>
            </div>
          ) : (
            <div className="space-y-3">
              {cart?.items?.map((item, idx) => (
                <div 
                  key={item.id || idx}
                  className={`flex items-center justify-between p-3 rounded-lg ${isDark ? 'bg-slate-700/50' : 'bg-slate-100'}`}
                >
                  <div>
                    <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      {item.player_data?.first_name} {item.player_data?.last_name}
                    </p>
                    <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                      {item.team_name} • {item.sport_name}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`font-bold ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>
                      ${item.amount?.toFixed(2)}
                    </span>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => removeFromCart(item.id)}
                      className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
              
              {/* Discounts */}
              {cart?.discounts?.length > 0 && (
                <div className={`p-3 rounded-lg ${isDark ? 'bg-green-900/20' : 'bg-green-50'}`}>
                  {cart.discounts.map((d, idx) => (
                    <div key={idx} className={`flex justify-between ${isDark ? 'text-green-400' : 'text-green-700'}`}>
                      <span>{d.description}</span>
                      <span>-${d.amount?.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Totals */}
              <div className={`border-t pt-3 ${isDark ? 'border-slate-600' : 'border-slate-200'}`}>
                <div className={`flex justify-between ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                  <span>Subtotal</span>
                  <span>${cart?.subtotal?.toFixed(2)}</span>
                </div>
                <div className={`flex justify-between text-lg font-bold mt-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  <span>Total</span>
                  <span>${cart?.total?.toFixed(2)}</span>
                </div>
              </div>
              
              <div className="flex justify-between pt-4">
                <Button 
                  variant="ghost" 
                  onClick={() => setStep('add-player')}
                >
                  <Plus className="h-4 w-4 mr-1" /> Add Another
                </Button>
                <Button 
                  onClick={() => setStep('family-info')}
                  className="bg-amber-600 hover:bg-amber-700"
                >
                  Continue
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
